import React, { useMemo, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardMedia,
  Avatar,
  Typography,
  Chip,
  Grid,
  Paper,
  Stack,
  Dialog,
  DialogContent,
  IconButton,
} from '@mui/material';
import {
  Business as BusinessIcon,
  LocationOn as LocationIcon,
  People as PeopleIcon,
  Work as WorkIcon,
  PersonSearch as PersonSearchIcon,
  Visibility as VisibilityIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { CompanyBasicInfo, CompanyProfile } from '../../types/company-profile';
import { LinkifiedText } from '../Common/LinkifiedText';

type CompanyProfileDisplayVariant = 'management' | 'public';

interface CompanyProfileDisplayProps {
  company: CompanyBasicInfo;
  profile: CompanyProfile;
  variant?: CompanyProfileDisplayVariant;
}

export const CompanyProfileDisplay: React.FC<CompanyProfileDisplayProps> = ({
  company,
  profile,
  variant = 'management',
}) => {
  const [selectedImage, setSelectedImage] = useState<string | null>(null);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('ja-JP', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const handleImageClick = (imageUrl: string) => {
    setSelectedImage(imageUrl);
  };

  const handleCloseImage = () => {
    setSelectedImage(null);
  };

  // 要件定義準拠：シンプルな公開・非公開制御
  const getStatusColor = (isPublished: boolean) => {
    return isPublished ? 'success' : 'default';
  };

  const getStatusLabel = (isPublished: boolean) => {
    return isPublished ? '公開中' : '非公開';
  };

  const isPublicView = variant === 'public';
  const companyImages = profile.company_images || [];

  const heroImage = useMemo(() => companyImages[0], [companyImages]);

  const formattedViewCount = useMemo(() => {
    return new Intl.NumberFormat('ja-JP').format(profile.view_count || 0);
  }, [profile.view_count]);

  const formattedLastUpdated = useMemo(() => {
    return profile.last_updated ? formatDate(profile.last_updated) : '-';
  }, [profile.last_updated]);

  return (
    <Box sx={{ maxWidth: 960, mx: 'auto', pb: 6 }}>
      {/* 企業基本情報 */}
      <Card
        sx={{
          mb: 3,
          borderRadius: 3,
          overflow: 'hidden',
          boxShadow: {
            xs: '0px 12px 28px rgba(15, 23, 42, 0.06)',
            md: '0px 18px 45px rgba(15, 23, 42, 0.08)',
          },
          border: '1px solid',
          borderColor: 'divider',
        }}
      >
        {heroImage && (
          <CardMedia
            component="img"
            image={heroImage}
            alt={`${company.name}のメイン画像`}
            sx={{
              height: { xs: 200, sm: 240, md: 260 },
              objectFit: 'cover',
            }}
          />
        )}
        <CardContent sx={{ p: { xs: 3, md: 4 } }}>
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={8}>
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={{ xs: 2.5, sm: 3 }}
                alignItems={{ xs: 'flex-start', sm: 'center' }}
              >
                <Avatar
                  src={profile.logo_url || company.logo_url}
                  alt={`${company.name}のロゴ`}
                  sx={{
                    width: { xs: 80, sm: 96 },
                    height: { xs: 80, sm: 96 },
                    bgcolor: 'grey.100',
                    border: '2px solid',
                    borderColor: heroImage ? 'common.white' : 'divider',
                    boxShadow: heroImage
                      ? '0px 10px 25px rgba(15, 23, 42, 0.18)'
                      : 'none',
                  }}
                >
                  <BusinessIcon sx={{ fontSize: 42, color: 'grey.500' }} />
                </Avatar>

                <Box sx={{ flexGrow: 1 }}>
                  <Typography
                    variant="h4"
                    component="h1"
                    sx={{ fontWeight: 700, mb: 1 }}
                  >
                    {company.name}
                  </Typography>
                  <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <WorkIcon
                        sx={{ fontSize: 20, color: 'text.secondary' }}
                      />
                      <Typography variant="body2" color="text.secondary">
                        {company.business_type}
                      </Typography>
                    </Stack>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <LocationIcon
                        sx={{ fontSize: 20, color: 'text.secondary' }}
                      />
                      <Typography variant="body2" color="text.secondary">
                        {company.prefecture} {company.city}
                      </Typography>
                    </Stack>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <PeopleIcon
                        sx={{ fontSize: 20, color: 'text.secondary' }}
                      />
                      <Typography variant="body2" color="text.secondary">
                        従業員数: {company.employee_count_range}
                      </Typography>
                    </Stack>
                  </Stack>
                </Box>
              </Stack>
            </Grid>

            <Grid item xs={12} md={4}>
              <Stack
                spacing={1.5}
                alignItems={{ xs: 'flex-start', md: 'flex-end' }}
              >
                {!isPublicView && (
                  <Chip
                    label={getStatusLabel(profile.is_published)}
                    color={getStatusColor(profile.is_published)}
                    variant="filled"
                    sx={{ fontWeight: 600 }}
                  />
                )}

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <VisibilityIcon
                    sx={{ fontSize: 18, color: 'text.secondary' }}
                  />
                  <Typography variant="body2" color="text.secondary">
                    閲覧数: {formattedViewCount} 回
                  </Typography>
                </Box>

                <Typography variant="caption" color="text.secondary">
                  最終更新: {formattedLastUpdated}
                </Typography>
              </Stack>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* 企業情報詳細 */}
      <Grid container spacing={3}>
        {/* 企業プロフィール画像 */}
        {companyImages.length > 0 && (
          <Grid item xs={12}>
            <Card
              variant="outlined"
              sx={{
                borderRadius: 3,
                boxShadow: '0px 8px 24px rgba(15, 23, 42, 0.04)',
              }}
            >
              <CardContent sx={{ p: { xs: 3, md: 3.5 } }}>
                <Typography
                  variant="h6"
                  component="h2"
                  gutterBottom
                  sx={{ fontWeight: 700 }}
                >
                  企業ギャラリー
                </Typography>
                <Grid container spacing={2}>
                  {companyImages.map((imageUrl, index) => (
                    <Grid item xs={12} sm={6} md={4} key={index}>
                      <Box
                        component="img"
                        src={imageUrl}
                        alt={`${company.name} 画像 ${index + 1}`}
                        onClick={() => handleImageClick(imageUrl)}
                        sx={{
                          width: '100%',
                          height: 220,
                          objectFit: 'cover',
                          borderRadius: 2,
                          boxShadow: '0px 8px 18px rgba(15, 23, 42, 0.08)',
                          cursor: 'pointer',
                          transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
                          '&:hover': {
                            transform: 'scale(1.02)',
                            boxShadow: '0px 12px 24px rgba(15, 23, 42, 0.15)',
                          },
                        }}
                      />
                    </Grid>
                  ))}
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* 事業内容・強み */}
        {profile.business_content && (
          <Grid item xs={12}>
            <Card
              variant="outlined"
              sx={{
                borderRadius: 3,
                boxShadow: '0px 8px 24px rgba(15, 23, 42, 0.04)',
              }}
            >
              <CardContent sx={{ p: { xs: 3, md: 3.5 } }}>
                <Typography
                  variant="h6"
                  component="h2"
                  gutterBottom
                  sx={{ fontWeight: 700 }}
                >
                  事業内容・強み
                </Typography>
                <LinkifiedText
                  text={profile.business_content}
                  variant="body1"
                  sx={{ lineHeight: 1.7, whiteSpace: 'pre-wrap' }}
                />
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* 採用メッセージ */}
        {profile.recruiting_message && (
          <Grid item xs={12}>
            <Card
              variant="outlined"
              sx={{
                borderRadius: 3,
                boxShadow: '0px 8px 24px rgba(15, 23, 42, 0.04)',
              }}
            >
              <CardContent sx={{ p: { xs: 3, md: 3.5 } }}>
                <Typography
                  variant="h6"
                  component="h2"
                  gutterBottom
                  sx={{ fontWeight: 700 }}
                >
                  採用メッセージ
                </Typography>
                <LinkifiedText
                  text={profile.recruiting_message}
                  variant="body1"
                  sx={{ lineHeight: 1.7, whiteSpace: 'pre-wrap' }}
                />
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* 募集職種 */}
        {profile.job_types && profile.job_types.length > 0 && (
          <Grid item xs={12} md={6}>
            <Card
              variant="outlined"
              sx={{
                borderRadius: 3,
                height: '100%',
                boxShadow: '0px 8px 24px rgba(15, 23, 42, 0.04)',
              }}
            >
              <CardContent sx={{ p: { xs: 3, md: 3.5 } }}>
                <Typography
                  variant="h6"
                  component="h2"
                  gutterBottom
                  sx={{ fontWeight: 700 }}
                >
                  募集職種
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {profile.job_types.map((jobType, index) => (
                    <Chip
                      key={index}
                      label={jobType}
                      variant="outlined"
                      color="primary"
                      sx={{ fontWeight: 500 }}
                    />
                  ))}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* 求める人材像 */}
        {profile.target_candidates && (
          <Grid item xs={12} md={6}>
            <Card
              variant="outlined"
              sx={{
                borderRadius: 3,
                height: '100%',
                boxShadow: '0px 8px 24px rgba(15, 23, 42, 0.04)',
              }}
            >
              <CardContent sx={{ p: { xs: 3, md: 3.5 } }}>
                <Typography
                  variant="h6"
                  component="h2"
                  gutterBottom
                  sx={{ fontWeight: 700 }}
                >
                  <PersonSearchIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                  求める人材像
                </Typography>
                <LinkifiedText
                  text={profile.target_candidates}
                  variant="body1"
                  sx={{ lineHeight: 1.7, whiteSpace: 'pre-wrap' }}
                />
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>

      {/* 更新情報 */}
      <Paper
        elevation={0}
        sx={{
          p: 2.5,
          mt: 3,
          backgroundColor: 'grey.50',
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'grey.100',
        }}
      >
        <Typography variant="caption" color="text.secondary">
          最終更新: {formattedLastUpdated}
        </Typography>
      </Paper>

      {/* 画像拡大表示ダイアログ */}
      <Dialog
        open={!!selectedImage}
        onClose={handleCloseImage}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: 'rgba(0, 0, 0, 0.9)',
            boxShadow: 'none',
          },
        }}
      >
        <IconButton
          onClick={handleCloseImage}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: 'white',
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.7)',
            },
            zIndex: 1,
          }}
        >
          <CloseIcon />
        </IconButton>
        <DialogContent
          sx={{
            p: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '80vh',
          }}
        >
          {selectedImage && (
            <Box
              component="img"
              src={selectedImage}
              alt="拡大画像"
              sx={{
                maxWidth: '100%',
                maxHeight: '90vh',
                objectFit: 'contain',
              }}
            />
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};
